import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, Edit2, Trash2, Save, X, Loader2, 
  Eye, EyeOff, Shield, Star, StarOff, Building2, Image, ChevronRight
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabase } from '../../lib/supabase';

const AdminReferences = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  
  const [references, setReferences] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    slug: '',
    title_tr: '',
    title_en: '',
    client_name: '',
    client_logo_url: '',
    excerpt_tr: '',
    excerpt_en: '',
    content_tr: '',
    content_en: '',
    featured_image_url: '',
    gallery_images: '[]',
    services_used: [],
    technologies: '',
    metrics: '{}',
    project_url: '',
    completed_at: '',
    is_featured: false,
    is_active: true
  });

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/auth/login');
        return;
      }
      checkAdminRole();
    }
  }, [user, authLoading, navigate]);

  const checkAdminRole = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();

      const role = profile?.role || 'customer';
      setUserRole(role);

      if (role === 'admin' || role === 'editor') {
        loadReferences();
        loadServices();
      }
    } catch (err) {
      console.error('Role check error:', err);
      setUserRole('customer');
    }
  };

  const loadReferences = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('our_references')
        .select('*')
        .order('created_at', { ascending: false });

      if (!error) setReferences(data || []);
    } catch (error) {
      console.error('Error loading references:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      const { data } = await supabase
        .from('services')
        .select('slug, title_tr, title_en')
        .eq('is_active', true);
      if (data) setServices(data);
    } catch (err) {
      console.error('Error loading services:', err);
    }
  };

  const handleEdit = (ref) => {
    setEditingId(ref.id);
    setIsCreating(false);
    setFormData({
      slug: ref.slug || '',
      title_tr: ref.title_tr || '',
      title_en: ref.title_en || '',
      client_name: ref.client_name || '',
      client_logo_url: ref.client_logo_url || '',
      excerpt_tr: ref.excerpt_tr || '',
      excerpt_en: ref.excerpt_en || '',
      content_tr: ref.content_tr || '',
      content_en: ref.content_en || '',
      featured_image_url: ref.featured_image_url || '',
      gallery_images: JSON.stringify(ref.gallery_images || [], null, 2),
      services_used: ref.services_used || [],
      technologies: (ref.technologies || []).join(', '),
      metrics: JSON.stringify(ref.metrics || {}, null, 2),
      project_url: ref.project_url || '',
      completed_at: ref.completed_at ? ref.completed_at.split('T')[0] : '',
      is_featured: ref.is_featured || false,
      is_active: ref.is_active !== false
    });
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingId(null);
    setFormData({
      slug: '',
      title_tr: '',
      title_en: '',
      client_name: '',
      client_logo_url: '',
      excerpt_tr: '',
      excerpt_en: '',
      content_tr: '',
      content_en: '',
      featured_image_url: '',
      gallery_images: '[]',
      services_used: [],
      technologies: '',
      metrics: '{}',
      project_url: '',
      completed_at: '',
      is_featured: false,
      is_active: true
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsCreating(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let gallery_images, metrics;
      try {
        gallery_images = JSON.parse(formData.gallery_images);
        metrics = JSON.parse(formData.metrics);
      } catch (e) {
        alert('Invalid JSON in gallery_images or metrics field');
        setSaving(false);
        return;
      }

      const technologies = formData.technologies
        .split(',')
        .map(t => t.trim())
        .filter(t => t);

      const saveData = {
        slug: formData.slug,
        title_tr: formData.title_tr,
        title_en: formData.title_en,
        client_name: formData.client_name,
        client_logo_url: formData.client_logo_url || null,
        excerpt_tr: formData.excerpt_tr,
        excerpt_en: formData.excerpt_en,
        content_tr: formData.content_tr,
        content_en: formData.content_en,
        featured_image_url: formData.featured_image_url || null,
        gallery_images,
        services_used: formData.services_used,
        technologies,
        metrics,
        project_url: formData.project_url || null,
        completed_at: formData.completed_at || null,
        is_featured: formData.is_featured,
        is_active: formData.is_active,
        updated_at: new Date().toISOString()
      };

      if (isCreating) {
        await supabase.from('our_references').insert(saveData);
      } else {
        await supabase.from('our_references').update(saveData).eq('id', editingId);
      }

      await loadReferences();
      handleCancel();
    } catch (error) {
      console.error('Error saving:', error);
      if (error.message?.includes('body stream')) {
        await loadReferences();
        handleCancel();
      } else {
        alert('Error saving: ' + error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(language === 'tr' ? 'Bu referansı silmek istediğinizden emin misiniz?' : 'Are you sure you want to delete this reference?')) {
      return;
    }
    try {
      await supabase.from('our_references').delete().eq('id', id);
      await loadReferences();
    } catch (error) {
      console.error('Error deleting:', error);
      await loadReferences();
    }
  };

  const toggleActive = async (id, currentState) => {
    try {
      await supabase.from('our_references').update({ is_active: !currentState }).eq('id', id);
      await loadReferences();
    } catch (error) {
      console.error('Error toggling:', error);
      await loadReferences();
    }
  };

  const toggleFeatured = async (id, currentState) => {
    try {
      await supabase.from('our_references').update({ is_featured: !currentState }).eq('id', id);
      await loadReferences();
    } catch (error) {
      console.error('Error toggling featured:', error);
      await loadReferences();
    }
  };

  const handleServiceToggle = (slug) => {
    const current = formData.services_used || [];
    if (current.includes(slug)) {
      setFormData({ ...formData, services_used: current.filter(s => s !== slug) });
    } else {
      setFormData({ ...formData, services_used: [...current, slug] });
    }
  };

  const texts = {
    tr: {
      title: 'Referans Yönetimi',
      addNew: 'Yeni Referans',
      edit: 'Düzenle',
      delete: 'Sil',
      save: 'Kaydet',
      cancel: 'İptal',
      slug: 'URL Slug',
      titleTr: 'Başlık (TR)',
      titleEn: 'Başlık (EN)',
      clientName: 'Müşteri Adı',
      clientLogo: 'Müşteri Logo URL',
      excerptTr: 'Özet (TR)',
      excerptEn: 'Özet (EN)',
      contentTr: 'İçerik (TR)',
      contentEn: 'İçerik (EN)',
      featuredImage: 'Öne Çıkan Görsel URL',
      galleryImages: 'Galeri Görselleri (JSON Array)',
      servicesUsed: 'Kullanılan Hizmetler',
      technologies: 'Teknolojiler (virgülle ayırın)',
      metrics: 'Metrikler (JSON Object)',
      projectUrl: 'Proje URL',
      completedAt: 'Tamamlanma Tarihi',
      featured: 'Öne Çıkan',
      active: 'Aktif',
      noReferences: 'Henüz referans yok'
    },
    en: {
      title: 'Reference Management',
      addNew: 'New Reference',
      edit: 'Edit',
      delete: 'Delete',
      save: 'Save',
      cancel: 'Cancel',
      slug: 'URL Slug',
      titleTr: 'Title (TR)',
      titleEn: 'Title (EN)',
      clientName: 'Client Name',
      clientLogo: 'Client Logo URL',
      excerptTr: 'Excerpt (TR)',
      excerptEn: 'Excerpt (EN)',
      contentTr: 'Content (TR)',
      contentEn: 'Content (EN)',
      featuredImage: 'Featured Image URL',
      galleryImages: 'Gallery Images (JSON Array)',
      servicesUsed: 'Services Used',
      technologies: 'Technologies (comma-separated)',
      metrics: 'Metrics (JSON Object)',
      projectUrl: 'Project URL',
      completedAt: 'Completion Date',
      featured: 'Featured',
      active: 'Active',
      noReferences: 'No references yet'
    }
  };

  const t = texts[language] || texts['tr'];

  if (authLoading || (user && userRole === null)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (userRole && userRole !== 'admin' && userRole !== 'editor') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {language === 'tr' ? 'Erişim Reddedildi' : 'Access Denied'}
          </h1>
          <button onClick={() => navigate('/')} className="mt-4 px-6 py-3 bg-indigo-600 text-white rounded-xl">
            {language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950" data-testid="admin-references-page">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Breadcrumb */}
          <nav className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            <Link to="/" className="hover:text-indigo-600 dark:hover:text-indigo-400">
              {language === 'tr' ? 'Ana Sayfa' : 'Home'}
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/admin" className="hover:text-indigo-600 dark:hover:text-indigo-400">
              Admin
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-900 dark:text-white font-medium">{t.title}</span>
          </nav>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-indigo-600" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t.title}</h1>
            </div>
            <button
              onClick={handleCreate}
              disabled={isCreating || editingId}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              data-testid="add-reference-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t.addNew}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Create/Edit Form */}
        {(isCreating || editingId) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-8"
            data-testid="reference-form"
          >
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6">
              {isCreating ? t.addNew : t.edit}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.slug}</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
                  placeholder="company-website"
                  data-testid="reference-slug-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.clientName}</label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
                  data-testid="reference-client-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.titleTr}</label>
                <input
                  type="text"
                  value={formData.title_tr}
                  onChange={(e) => setFormData({ ...formData, title_tr: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
                  data-testid="reference-title-tr-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.titleEn}</label>
                <input
                  type="text"
                  value={formData.title_en}
                  onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
                  data-testid="reference-title-en-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.clientLogo}</label>
                <input
                  type="text"
                  value={formData.client_logo_url}
                  onChange={(e) => setFormData({ ...formData, client_logo_url: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.featuredImage}</label>
                <input
                  type="text"
                  value={formData.featured_image_url}
                  onChange={(e) => setFormData({ ...formData, featured_image_url: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.excerptTr}</label>
                <textarea
                  rows={2}
                  value={formData.excerpt_tr}
                  onChange={(e) => setFormData({ ...formData, excerpt_tr: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg resize-none text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.excerptEn}</label>
                <textarea
                  rows={2}
                  value={formData.excerpt_en}
                  onChange={(e) => setFormData({ ...formData, excerpt_en: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg resize-none text-gray-900 dark:text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.contentTr}</label>
                <textarea
                  rows={4}
                  value={formData.content_tr}
                  onChange={(e) => setFormData({ ...formData, content_tr: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg resize-none text-gray-900 dark:text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.contentEn}</label>
                <textarea
                  rows={4}
                  value={formData.content_en}
                  onChange={(e) => setFormData({ ...formData, content_en: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg resize-none text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.technologies}</label>
                <input
                  type="text"
                  value={formData.technologies}
                  onChange={(e) => setFormData({ ...formData, technologies: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
                  placeholder="React, Node.js, PostgreSQL"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.projectUrl}</label>
                <input
                  type="text"
                  value={formData.project_url}
                  onChange={(e) => setFormData({ ...formData, project_url: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
                  placeholder="https://client-website.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.completedAt}</label>
                <input
                  type="date"
                  value={formData.completed_at}
                  onChange={(e) => setFormData({ ...formData, completed_at: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.metrics}</label>
                <textarea
                  rows={3}
                  value={formData.metrics}
                  onChange={(e) => setFormData({ ...formData, metrics: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-mono text-sm resize-none text-gray-900 dark:text-white"
                  placeholder='{"Traffic Increase": "+150%", "Conversion": "+30%"}'
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t.servicesUsed}</label>
                <div className="flex flex-wrap gap-2">
                  {services.map(service => (
                    <button
                      key={service.slug}
                      type="button"
                      onClick={() => handleServiceToggle(service.slug)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                        formData.services_used.includes(service.slug)
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      {language === 'tr' ? service.title_tr : service.title_en}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.galleryImages}</label>
                <textarea
                  rows={3}
                  value={formData.gallery_images}
                  onChange={(e) => setFormData({ ...formData, gallery_images: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-mono text-sm resize-none text-gray-900 dark:text-white"
                  placeholder='["https://image1.jpg", "https://image2.jpg"]'
                />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t.featured}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t.active}</span>
                </label>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                data-testid="save-reference-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {t.save}
              </button>
              <button
                onClick={handleCancel}
                className="inline-flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg"
                data-testid="cancel-reference-btn"
              >
                <X className="w-4 h-4 mr-2" />{t.cancel}
              </button>
            </div>
          </motion.div>
        )}

        {/* References List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : references.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-12 text-center">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">{t.noReferences}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="references-admin-list">
            {references.map((ref) => (
              <motion.div
                key={ref.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white dark:bg-gray-900 border rounded-xl overflow-hidden ${
                  ref.is_active ? 'border-gray-200 dark:border-gray-800' : 'border-red-200 dark:border-red-900 opacity-60'
                }`}
                data-testid={`reference-admin-card-${ref.slug}`}
              >
                {/* Image */}
                <div className="aspect-video bg-gray-100 dark:bg-gray-800 relative">
                  {ref.featured_image_url ? (
                    <img 
                      src={ref.featured_image_url} 
                      alt={ref.title_tr}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-10 h-10 text-gray-400" />
                    </div>
                  )}
                  {ref.is_featured && (
                    <div className="absolute top-2 left-2 p-1 bg-yellow-500 rounded">
                      <Star className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <Building2 className="w-4 h-4" />
                    <span>{ref.client_name}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-1">{ref.title_tr}</h3>
                  <p className="text-sm text-gray-500">{ref.title_en}</p>
                </div>

                {/* Actions */}
                <div className="px-4 pb-4 flex items-center justify-end gap-2">
                  <button
                    onClick={() => toggleFeatured(ref.id, ref.is_featured)}
                    className={`p-2 rounded-lg ${ref.is_featured ? 'text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/20' : 'text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                    title={ref.is_featured ? 'Featured' : 'Not Featured'}
                  >
                    {ref.is_featured ? <Star className="w-5 h-5" /> : <StarOff className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => toggleActive(ref.id, ref.is_active)}
                    className={`p-2 rounded-lg ${ref.is_active ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20' : 'text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                  >
                    {ref.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => handleEdit(ref)}
                    className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(ref.id)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReferences;
